import React, { useState } from "react";
import { loginUser } from "../api";

export default function Login({ onLoginSuccess }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!username.trim() || !password.trim()) {
      setError("Please fill in all fields.");
      return;
    }

    setIsLoading(true);

    try {
      const data = await loginUser(username.trim(), password);
      setIsLoading(false);
      localStorage.setItem("cropscan_authenticated", "true");
      localStorage.setItem("cropscan_session_id", data.session_id);
      localStorage.setItem("cropscan_role", data.role || "user");
      onLoginSuccess(data.session_id, data.role || "user");
    } catch (err) {
      setIsLoading(false);
      if (err.response && err.response.status === 401) {
        setError("Invalid username or password. Please try again.");
      } else {
        setError("Unable to connect to the backend server. Please verify it is running.");
      }
    }
  };

  return (
    <div 
      className="min-h-screen w-screen flex items-center justify-center relative overflow-hidden px-[5vw] font-body-lg"
      style={{
        backgroundImage: `url(${process.env.PUBLIC_URL}/login_bg.jpg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      {/* Dark overlay backdrop to ensure high contrast and readability */}
      <div className="absolute inset-0 bg-black/65 dark:bg-black/80 backdrop-blur-xs pointer-events-none" />
      
      {/* Decorative organic background blobs */}
      <div className="absolute w-[35vw] h-[35vw] max-w-[500px] max-h-[500px] rounded-full bg-primary/10 blur-[100px] -top-32 -left-20 animate-pulse duration-10000 pointer-events-none" />
      <div className="absolute w-[30vw] h-[30vw] max-w-[400px] max-h-[400px] rounded-full bg-secondary-fixed-dim/10 blur-[90px] -bottom-20 -right-10 pointer-events-none" />

      {/* Split-pane outer container */}
      <div className="w-full flex flex-col lg:flex-row items-center justify-between gap-[5vw] relative z-10 py-[4vh]">
        
        {/* Left Section - Branded Info & Logos */}
        <div className="flex flex-col items-center lg:items-start text-center lg:text-left text-white w-full lg:w-[48%]">
          {/* Separate, larger logo containers */}
          <div className="grid grid-cols-2 gap-[2vw] mb-[3vh] select-none">
            <div className="flex items-center justify-center hover:scale-105 transition-all duration-300">
              <img
                src={process.env.PUBLIC_URL + "/cmmi.png"}
                alt="Niruthi Logo"
                className="max-h-[13vh] max-w-full object-contain"
              />
            </div>
            <div className="flex items-center justify-center hover:scale-105 transition-all duration-300">
              <img
                src={process.env.PUBLIC_URL + "/gpw.png"}
                alt="Experiment Logo"
                className="max-h-[13vh] max-w-full object-contain"
              />
            </div>
            <div className="bg-white/95 p-[1vw] rounded-[1.5vw] shadow-xl hover:scale-105 transition-all duration-300 border border-white/10 flex items-center justify-center h-[13vh] w-[22vw] shrink-0">
              <img
                src={process.env.PUBLIC_URL + "/niruthi_logo.png"}
                alt="Niruthi Logo"
                className="h-[75%] w-full object-contain"
              />
            </div>
            <div className="bg-white/95 p-[1vw] rounded-[1.5vw] shadow-xl hover:scale-105 transition-all duration-300 border border-white/10 flex items-center justify-center h-[13vh] w-[22vw] shrink-0">
              <img
                src={process.env.PUBLIC_URL + "/exp.png"}
                alt="Experiment Logo"
                className="h-[75%] w-full object-contain"
              />
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-[3.5vw] font-extrabold tracking-tight mb-[1vh] drop-shadow-md">
            CROPSCAN
          </h1>
          <p className="text-primary-fixed-dim text-lg sm:text-xl lg:text-[1.3vw] font-semibold mb-[1.5vh] tracking-wide uppercase">
            For better Agriculture
          </p>
          <p className="text-white/70 text-sm sm:text-base lg:text-[1.05vw] leading-relaxed max-w-md lg:max-w-none">
            Empowering modern agriculture with computer vision models optimized for real-time crop identification, disease and pest tackling suggestions, and crop stage monitoring.
          </p>
        </div>

        {/* Right Section - Login Glass Card */}
        <div className="w-full lg:w-[30vw] glass-card rounded-[1.8vw] p-[2vw] border border-white/20 dark:border-white/10 shadow-2xl shrink-0">
          
          <div className="mb-[2.5vh]">
            <h2 className="text-2xl lg:text-[1.8vw] font-bold text-white mb-[0.5vh] leading-tight">
              Sign In
            </h2>
            <p className="text-white/60 text-xs lg:text-[0.9vw]">
              Enter your credentials to access the Application
            </p>
          </div>

          {/* Error Alert Box */}
          {error && (
            <div className="mb-[2vh] p-[1.2vw] rounded-[1vw] bg-error-container text-on-error-container text-xs lg:text-[0.9vw] flex items-center gap-[0.8vw] border border-error/20 animate-in fade-in slide-in-from-top-2 duration-200">
              <span className="material-symbols-outlined text-error shrink-0 text-[1.4vw]">warning</span>
              <span>{error}</span>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="flex flex-col gap-[2vh]">
            {/* Username Input */}
            <div className="flex flex-col gap-[0.8vh]">
              <label className="text-white/80 font-medium text-xs lg:text-[0.9vw] px-[0.2vw]">Username</label>
              <div className="relative flex items-center">
                <span className="material-symbols-outlined absolute left-[1vw] text-white/50 text-[1.5vw] select-none">
                  person
                </span>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter username"
                  className="w-full pl-[3vw] pr-[1vw] py-[1.2vh] bg-white/10 dark:bg-black/20 text-white placeholder-white/40 border border-white/20 dark:border-white/10 rounded-[1vw] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 text-sm sm:text-base lg:text-[1vw]"
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Password Input */}
            <div className="flex flex-col gap-[0.8vh]">
              <label className="text-white/80 font-medium text-xs lg:text-[0.9vw] px-[0.2vw]">Password</label>
              <div className="relative flex items-center">
                <span className="material-symbols-outlined absolute left-[1vw] text-white/50 text-[1.5vw] select-none">
                  lock
                </span>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="w-full pl-[3vw] pr-[3vw] py-[1.2vh] bg-white/10 dark:bg-black/20 text-white placeholder-white/40 border border-white/20 dark:border-white/10 rounded-[1vw] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 text-sm sm:text-base lg:text-[1vw]"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-[1vw] p-[0.3vw] rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors"
                  disabled={isLoading}
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  <span className="material-symbols-outlined text-[1.4vw] block select-none">
                    {showPassword ? "visibility_off" : "visibility"}
                  </span>
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full mt-[1vh] py-[1.4vh] bg-primary text-white font-bold rounded-[1vw] shadow-lg shadow-primary/20 hover:bg-primary-container hover:shadow-primary-container/30 active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-[0.5vw] group relative overflow-hidden text-sm sm:text-base lg:text-[1.1vw]"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin h-[1.2vw] w-[1.2vw] text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <span>Verifying...</span>
                </>
              ) : (
                <>
                  <span>Sign In</span>
                  <span className="material-symbols-outlined text-[1.2vw] group-hover:translate-x-1 transition-transform">
                    arrow_forward
                  </span>
                </>
              )}
            </button>
          </form>

          {/* Footer info banner */}
          <div className="mt-[3vh] text-center text-xs lg:text-[0.8vw] text-white/40 border-t border-white/10 pt-[1.5vh]">
            <div>Proudly A Niruthi Product</div>
          </div>
        </div>

      </div>
    </div>
  );
}
